/**
 * Shared form-field primitives: the labelled controls the proposal wizard
 * renders, plus the pieces FieldRenderer reuses (help pill, line-list editor).
 */
import type { ReactNode } from 'react'
import { ExpandableTextarea } from './ExpandableTextarea'
import { fieldTestId } from './fieldTestId'
import { HelpTooltip } from './HelpTooltip'
import { PriorityBadge } from './PriorityBadge'
import type { ProposalHelp } from '../schema/proposalHelp'

/** The "i" help pill beside a label; renders nothing without a hint or body. */
export function FieldHelp({ label, hint, help }: { label: string; hint?: string | undefined; help?: string | undefined }) {
  if (!hint && !help) return null
  return <HelpTooltip content={hint ?? ''} help={help} label={label} ariaLabel={`Help for ${label}`} />
}

/** One-entry-per-line textarea over a string[]. Keystrokes stay verbatim while
 *  typing (blank lines, trailing spaces) — trimming on change fought the
 *  controlled re-render — and the list is canonicalized on blur. */
export function LinesTextarea({
  label,
  value,
  placeholder,
  testId,
  onChange,
}: {
  label: string
  value: string[]
  placeholder?: string | undefined
  testId?: string | undefined
  onChange: (v: string[]) => void
}) {
  const text = value.join('\n')
  return (
    <ExpandableTextarea
      label={label}
      value={text}
      placeholder={placeholder}
      rows={Math.max(3, value.length + 1)}
      testId={testId}
      onChange={(v) => onChange(v.split('\n'))}
      onBlur={() => onChange(text.split('\n').map((l) => l.trim()).filter(Boolean))}
    />
  )
}

export function FieldLabel({ children, help }: { children: ReactNode; help?: ProposalHelp | undefined }) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        fontSize: '0.78rem',
        color: 'var(--muted)',
        marginBottom: '0.15rem',
        fontWeight: 600,
        letterSpacing: '0.01em',
      }}
    >
      <span>{children}</span>
      <PriorityBadge priority={help?.priority} />
      <FieldHelp label={String(children)} hint={help?.summary} help={help?.body} />
    </label>
  )
}

/** A labelled wizard field: label + help pill above the control. */
function Labeled({ label, help, children }: { label: string; help?: ProposalHelp | undefined; children: ReactNode }) {
  return (
    <div style={{ marginBottom: '1.1rem' }}>
      <FieldLabel help={help}>{label}</FieldLabel>
      {children}
    </div>
  )
}

interface FieldProps<V> {
  label: string
  value: V
  help?: ProposalHelp | undefined
  onChange: (v: V) => void
}

export function TextField({
  label,
  value,
  placeholder,
  help,
  onChange,
  onBlur,
}: FieldProps<string> & { placeholder?: string | undefined; onBlur?: (() => void) | undefined }) {
  return (
    <Labeled label={label} help={help}>
      <input type="text" value={value} placeholder={placeholder} data-testid={fieldTestId(label)} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} />
    </Labeled>
  )
}

/** Numeric field bound to a `number | null` value (blank → null). Commits on change. */
export function NumberField({ label, value, placeholder, help, onChange }: FieldProps<number | null> & { placeholder?: string | undefined }) {
  return (
    <Labeled label={label} help={help}>
      <input
        type="number"
        value={value ?? ''}
        placeholder={placeholder}
        data-testid={fieldTestId(label)}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      />
    </Labeled>
  )
}

export function TextareaField({
  label,
  value,
  rows,
  help,
  onChange,
  onBlur,
}: FieldProps<string> & { rows: number; onBlur?: (() => void) | undefined }) {
  return (
    <Labeled label={label} help={help}>
      <ExpandableTextarea
        label={label}
        value={value}
        rows={rows * 2} // doubled — the literal heights were cramped for free-form text
        testId={fieldTestId(label)}
        onChange={onChange}
        onBlur={onBlur}
      />
    </Labeled>
  )
}

export function RadioField({
  label,
  value,
  options,
  help,
  onChange,
}: FieldProps<string> & { options: ReadonlyArray<{ value: string; label: string }> }) {
  return (
    <Labeled label={label} help={help}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', marginTop: '0.15rem' }}>
        {options.map((o) => (
          <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem' }}>
            <input type="radio" checked={value === o.value} data-testid={`${fieldTestId(label)}-${o.value}`} onChange={() => onChange(o.value)} />
            {o.label}
          </label>
        ))}
      </div>
    </Labeled>
  )
}

export function ListField({ label, value, help, onChange }: FieldProps<string[]>) {
  return (
    <Labeled label={label} help={help}>
      <LinesTextarea label={label} value={value} testId={fieldTestId(label)} onChange={onChange} />
    </Labeled>
  )
}
